import type React from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

type Props = {
	secondsRemaining: number;
};

const formatRemaining = (seconds: number): string => `0:${String(seconds).padStart(2, '0')}`;

/**
 * Red dot and remaining time, over the preview while a take is rolling.
 *
 * Counts down where the recorder screen counts up. That is this arm's design, and it stays this arm's:
 * `RecorderTimeLimit` is untouched, so control, `v1_emotional` and `v3_impact` still read
 * "N of 30 seconds".
 */
export const RecordingBadge: React.FC<Props> = ({ secondsRemaining }) => {
	const { translatePlural } = useI18n();

	return (
		<div
			className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/40 px-2 py-1"
			data-testid="consolidated-recording-badge"
		>
			<span className="bg-fill-brand-strong h-2.5 w-2.5 animate-pulse rounded-full motion-reduce:animate-none" />
			{/*
			 * The digits are what a sighted supporter reads; the label is what the phrase sounds like, so a
			 * screen reader does not announce a bare "0:28".
			 */}
			<span
				className="text-xs font-semibold text-white tabular-nums"
				aria-label={translatePlural(
					'corgi.petition_actions.video_first_consolidated_ask.time_remaining',
					secondsRemaining,
					{ seconds: secondsRemaining },
				)}
			>
				{formatRemaining(secondsRemaining)}
			</span>
		</div>
	);
};
